import type { Font } from '../types/dbTypes';

// Track which fonts have been loaded via FontFace API
const loadedFontFamilies = new Set<string>();

/**
 * Generate CSS @font-face rules from fonts in database
 * This allows custom fonts to be loaded and used in SVG text elements
 */
export function generateFontFaceCss(fonts: Font[]): string {
    const fontFaceRules = fonts
        .filter(font => font.url) // Only fonts with data URIs
        .map(font => {
            return `
@font-face {
    font-family: '${font.family}';
    src: url('${font.url}');
    font-display: swap;
}`.trim();
        });

    return fontFaceRules.join('\n\n');
}

/**
 * Inject font CSS into document head
 * This makes fonts available to the entire application
 */
export function injectFontCss(fonts: Font[]): void {
    // Remove any existing font style element
    const existingStyle = document.getElementById('custom-fonts-style');
    if (existingStyle) {
        existingStyle.remove();
    }

    // Create new style element
    const styleEl = document.createElement('style');
    styleEl.id = 'custom-fonts-style';
    styleEl.innerHTML = generateFontFaceCss(fonts);
    document.head.appendChild(styleEl);
}

/**
 * Load fonts using FontFace API for reliable, synchronous loading
 * This ensures fonts are actually available before returning
 */
export async function loadFontsWithFontFace(fonts: Font[]): Promise<void> {
    const fontsToLoad = fonts.filter(f => f.url && !loadedFontFamilies.has(f.family));

    if (fontsToLoad.length === 0) {
        return;
    }

    const loadPromises = fontsToLoad.map(async (font) => {
        try {
            // Create FontFace object
            const fontFace = new FontFace(font.family, `url(${font.url})`);

            // Load the font
            const loadedFace = await fontFace.load();

            // Add to document fonts
            document.fonts.add(loadedFace);

            // Track as loaded
            loadedFontFamilies.add(font.family);

            console.log(`[FontLoader] Loaded font: ${font.family}`);
        } catch (err) {
            console.error(`[FontLoader] Failed to load font ${font.family}:`, err);
        }
    });

    await Promise.all(loadPromises);

    // Also inject CSS for fallback (SVG export, etc.)
    injectFontCss(fonts);
}

/**
 * Wait for all fonts to be loaded
 * Useful for ensuring fonts are ready before rendering
 */
export async function waitForFontsLoaded(): Promise<void> {
    if ('fonts' in document) {
        await document.fonts.ready;
    }
}

/**
 * Check if a specific font is loaded
 */
export function isFontLoaded(fontFamily: string): boolean {
    // Check our tracking set first (more reliable)
    if (loadedFontFamilies.has(fontFamily)) {
        return true;
    }

    // Fallback to document.fonts.check
    if (!('fonts' in document)) {
        return false;
    }

    return document.fonts.check(`12px "${fontFamily}"`);
}

/**
 * Clear the loaded fonts tracking (for testing/reset)
 */
export function clearLoadedFonts(): void {
    loadedFontFamilies.clear();
}
